import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import { Action } from 'redux';
import { ofType } from 'redux-observable';
import { EMPTY, Observable } from 'rxjs';
import { from } from 'rxjs/internal/observable/from';
import { of } from 'rxjs/internal/observable/of';
import { catchError, mergeMap } from 'rxjs/operators';

import { updateRegions } from '../../actions/common';
import { clearRegionsEditor, openRegionsEditor } from '../../actions/regionsEditor';
import { clearRegionsSearchEditor, initRegionsTransformEditor } from '../../actions/regionsSearchEditor';
import {
    APPLY_REGIONS_TRANSFORM_EDITOR,
    ApplyRegionsTransformEditor,
    CLEAR_REGIONS_TRANSFORM_EDITOR,
    ClearRegionsTransformEditor,
    closeRegionsTransformEditor,
    endApplyRegionsTransformEditor,
    FETCH_TOP_REGIONS_FOR_REGIONS_TRANSFORM_EDITOR,
    fetchTopRegionsForRegionsTransformEditor,
    OPEN_REGIONS_TRANSFORM_EDITOR,
    OpenRegionsTransformEditor,
    SHOW_NOTIFICATION_REGIONS_TRANSFORM_EDITOR,
    ShowNotificationRegionsTransformEditor,
    showNotificationRegionsTransformEditor,
    updateQuickSelectedItemsForRegionsTransformEditor,
    updateValidationErrorsTransformEditor
} from '../../actions/regionsTransformEditor';
import { GroupCriteriaKey, GroupCriteriaSetTitles } from '../../declarations/filter/filter';
import { expressionToString } from '../../mappers/filter/filter';
import { mapQuickSelectedItemsForRegionsTransformEditor } from '../../mappers/regionsTransformEditor/regionsTransformEditor';
import { selectRegionsEditor } from '../../selectors/groupEdit/group/group';
import { selectRegionsSearchEditorValue } from '../../selectors/regionsSearchEditor/regionsSearchEditor';
import {
    selectRegionsTransformEditorValue,
    selectRegionTreeForTransformEditor
} from '../../selectors/regionsTransformEditor/regionsTransformEditor';
import { ApiMethodExecutingError } from '../../service/Errors';
import { showNoticeMessage, showNoticeMessageWithLink } from '../../service/GridHelpers/GridHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { fireRegionsMassEditMetrika } from '../../service/Metrika/metrika';
import { getRequestResultNoticeParams } from '../../service/NoticeTypeHelper/NoticeTypeHelper';
import { getDefaultRegionsSearchEditorState, RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import {
    getNotificationMessageForRegionsTransformEditor,
    getRegionsEditorIdForRegionsTransform,
    TOP_REGIONS_COUNT_FOR_REGIONS_TRANSFORM
} from '../../service/RegionsTransformEditorHelper/RegionsTransformEditorHelper';
import { Session } from '../../service/Session/Session';
import { UrlHelpers } from '../../service/UrlHelpers';
import { groups, Region } from '../../typings/common';
import { DnaEpic, DnaEpicAction } from '../../typings/epic';
import { FilterExpressionValueType, Operator } from '../../typings/filter';
import {
    GdUpdateAdGroupRegionsActionInput,
    GdUpdateAdGroupRegionsInput,
    UpdateAdGroupRegions
} from '../../typings/graphql-grid';
import { GridAlertMessageType } from '../../typings/grid';

import * as keyset from './regionsTransformEditor.i18n';
const i18n = i18nFactory(keyset);

export const openRegionsTransformEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_REGIONS_TRANSFORM_EDITOR),
        mergeMap(
            (action: OpenRegionsTransformEditor): DnaEpicAction => {
                const { editorId } = action;
                const state = store$.value;
                const { translocalGeotreeType } = state.commonData.client;

                try {
                    fireRegionsMassEditMetrika({
                        openRegionsMassEdit: true
                    });
                } catch (e) {
                    // do nothing
                }

                return from(RegionsHelpers.fetchRegions()).pipe(
                    mergeMap(
                        (regionsResult): DnaEpicAction => {
                            const tree: Region[] = regionsResult
                                ? RegionsHelpers.getRegionsTree(
                                      regionsResult,
                                      LangUtils.getCurrentLang(),
                                      translocalGeotreeType
                                  )
                                : [];
                            const defaultRegionsSearchEditorState = getDefaultRegionsSearchEditorState();
                            const id = getRegionsEditorIdForRegionsTransform(
                                editorId,
                                GdUpdateAdGroupRegionsActionInput.REPLACE
                            );

                            return from([
                                updateRegions(regionsResult),
                                openRegionsEditor({ id, selectedMap: {}, tree }),
                                initRegionsTransformEditor(
                                    getRegionsEditorIdForRegionsTransform(
                                        editorId,
                                        GdUpdateAdGroupRegionsActionInput.REMOVE
                                    ),
                                    defaultRegionsSearchEditorState
                                ),
                                initRegionsTransformEditor(
                                    getRegionsEditorIdForRegionsTransform(
                                        editorId,
                                        GdUpdateAdGroupRegionsActionInput.ADD
                                    ),
                                    defaultRegionsSearchEditorState
                                ),
                                fetchTopRegionsForRegionsTransformEditor(editorId)
                            ]);
                        }
                    )
                );
            }
        )
    );
};

export const clearRegionsTransformEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CLEAR_REGIONS_TRANSFORM_EDITOR),
        mergeMap(
            (action: ClearRegionsTransformEditor): DnaEpicAction => {
                const { editorId } = action;
                const state = store$.value;
                const editorValue = selectRegionsTransformEditorValue(state, editorId);
                const regionTree = selectRegionTreeForTransformEditor(state);
                const { typeSelectedRegionsTransform } = editorValue;

                if (!regionTree) {
                    return EMPTY;
                }

                switch (typeSelectedRegionsTransform) {
                    case GdUpdateAdGroupRegionsActionInput.REPLACE:
                        return of(
                            clearRegionsEditor(
                                getRegionsEditorIdForRegionsTransform(editorId, typeSelectedRegionsTransform),
                                regionTree
                            )
                        );
                    case GdUpdateAdGroupRegionsActionInput.ADD:
                    case GdUpdateAdGroupRegionsActionInput.REMOVE:
                        return of(
                            clearRegionsSearchEditor(
                                getRegionsEditorIdForRegionsTransform(editorId, typeSelectedRegionsTransform)
                            )
                        );
                    default:
                        throw new Error(`Неподдерживаемый тип корректировки регионов: ${typeSelectedRegionsTransform}`);
                }
            }
        )
    );
};

export const applyRegionsTransformEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(APPLY_REGIONS_TRANSFORM_EDITOR),
        mergeMap(
            (action: ApplyRegionsTransformEditor): DnaEpicAction => {
                const { editorId } = action;
                const state = store$.value;
                const { login } = state.commonData.client;
                const editorValue = selectRegionsTransformEditorValue(state, editorId);
                const { typeSelectedRegionsTransform, campaignsIds, groupsIds = [] } = editorValue;
                const regionTree = selectRegionTreeForTransformEditor(state);

                if (!login || !regionTree || (groupsIds.length === 0 && campaignsIds.length === 0)) {
                    return EMPTY;
                }

                let changedRegionsIds: string[] = [];

                switch (typeSelectedRegionsTransform) {
                    case GdUpdateAdGroupRegionsActionInput.REPLACE:
                        const regionsEditorValue = selectRegionsEditor(
                            state,
                            getRegionsEditorIdForRegionsTransform(editorId, typeSelectedRegionsTransform)
                        );

                        if (regionsEditorValue) {
                            changedRegionsIds = RegionsHelpers.convertRegionsSelectedInfoToFlattenList(
                                RegionsHelpers.getSelectedRegionsInfo(regionTree, regionsEditorValue.selectedMap)
                            ).map(String);
                        }

                        break;
                    case GdUpdateAdGroupRegionsActionInput.ADD:
                    case GdUpdateAdGroupRegionsActionInput.REMOVE:
                        const regionsSearchEditorValue = selectRegionsSearchEditorValue(
                            state,
                            getRegionsEditorIdForRegionsTransform(editorId, typeSelectedRegionsTransform)
                        );

                        if (regionsSearchEditorValue) {
                            changedRegionsIds = regionsSearchEditorValue.selectedRegions.map(selectedRegion =>
                                String(selectedRegion.val)
                            );
                        }

                        break;
                    default:
                        changedRegionsIds = [];
                }

                if (changedRegionsIds.length === 0) {
                    return of(closeRegionsTransformEditor(editorId));
                }

                const requestRegionsInput: GdUpdateAdGroupRegionsInput = {
                    regionIds: changedRegionsIds,
                    action: typeSelectedRegionsTransform
                };

                if (groupsIds.length !== 0) {
                    requestRegionsInput.adGroupIds = groupsIds;
                } else if (campaignsIds.length !== 0) {
                    // редактируем гео на кампаниях (на самом деле вложенные группы)
                    requestRegionsInput.campaignIds = campaignsIds;
                }

                return from(deps.regionsMutationExecutor.updateAdGroupRegions(requestRegionsInput, login)).pipe(
                    mergeMap(
                        (resultUpdateRegions: FetchResult<UpdateAdGroupRegions>): DnaEpicAction => {
                            const responseData = resultUpdateRegions.data;

                            try {
                                fireRegionsMassEditMetrika({
                                    updateAdGroupRegions: {
                                        [typeSelectedRegionsTransform]: JSON.stringify(responseData)
                                    }
                                });
                            } catch (e) {
                                // do nothing
                            }

                            if (!responseData) {
                                throw new Error('Ошибка обновления регионов показа');
                            }

                            const successGroupsIds = responseData.updateAdGroupRegions.updatedAdGroupItems.reduce(
                                (acc: string[], groupItem) => {
                                    if (groupItem) {
                                        acc.push(String(groupItem.adGroupId));
                                    }

                                    return acc;
                                },
                                []
                            );
                            const skippedGroupsIds = responseData.updateAdGroupRegions.skippedAdGroupIds.map(String);

                            return of(
                                closeRegionsTransformEditor(editorId),
                                showNotificationRegionsTransformEditor(
                                    editorId,
                                    campaignsIds,
                                    skippedGroupsIds,
                                    successGroupsIds
                                )
                            );
                        }
                    ),
                    catchError(
                        (err: Error | ApiMethodExecutingError): Observable<Action> => {
                            const message = err instanceof ApiMethodExecutingError ? err.getMessage() : err.message;

                            try {
                                fireRegionsMassEditMetrika({
                                    updateAdGroupRegions: {
                                        errors: message
                                    }
                                });
                            } catch (e) {
                                // do nothing
                            }

                            return of(
                                updateValidationErrorsTransformEditor(editorId, [
                                    {
                                        code: 'SERVER_ERROR',
                                        text: i18n('common-error-update-regions')
                                    }
                                ]),
                                endApplyRegionsTransformEditor(editorId)
                            );
                        }
                    )
                );
            }
        )
    );
};

export const showNotificationRegionsTransformEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SHOW_NOTIFICATION_REGIONS_TRANSFORM_EDITOR),
        mergeMap(
            (action: ShowNotificationRegionsTransformEditor): DnaEpicAction => {
                const { campaignIds, skippedGroupsIds, successGroupsIds } = action;
                const countGroups = skippedGroupsIds.length + successGroupsIds.length;
                const countSuccessGroups = successGroupsIds.length;
                const state = store$.value;
                const {
                    commonData: { login }
                } = state;
                const { noticeType } = getRequestResultNoticeParams(countGroups, countSuccessGroups);
                const isAllGroupsSuccessApply = countGroups === countSuccessGroups;

                const noticeMessage = getNotificationMessageForRegionsTransformEditor(
                    countGroups,
                    countSuccessGroups,
                    !isAllGroupsSuccessApply
                );

                if (isAllGroupsSuccessApply) {
                    showNoticeMessage(noticeType, noticeMessage);
                } else {
                    const linkMessage = i18n('link-message-groups');
                    const link = UrlHelpers.getGroupsListUrl({
                        ulogin: login,
                        'message-type': GridAlertMessageType.regionsTransformError
                    });

                    const serializeFilterToErrorGroups = expressionToString({
                        field: GroupCriteriaSetTitles[GroupCriteriaKey.id],
                        operator: Operator.EQUALS,
                        value: {
                            type: FilterExpressionValueType.numberSet,
                            value: skippedGroupsIds
                        }
                    });

                    // Подготавливаем новую страницу перехода
                    Session.setValueToCurrentSession(login, {
                        alertMessageFilter: {
                            [groups]: serializeFilterToErrorGroups
                        },
                        campaignsIds: campaignIds
                    });

                    showNoticeMessageWithLink(noticeType, noticeMessage, link, linkMessage);
                }

                return EMPTY;
            }
        )
    );
};

export const fetchTopRegionsForRegionsTransformEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_TOP_REGIONS_FOR_REGIONS_TRANSFORM_EDITOR),
        mergeMap(
            (action: OpenRegionsTransformEditor): DnaEpicAction => {
                const { editorId } = action;
                const state = store$.value;
                const {
                    commonData: {
                        login,
                        regions,
                        client: { translocalGeotreeType }
                    }
                } = state;

                return from(
                    deps.regionsDataProvider.getTopRegions(login, TOP_REGIONS_COUNT_FOR_REGIONS_TRANSFORM)
                ).pipe(
                    mergeMap(
                        (topRegionPresetsResponse): DnaEpicAction => {
                            if (!regions) {
                                return EMPTY;
                            }

                            const regionTree: Region[] = regions
                                ? RegionsHelpers.getRegionsTree(
                                      regions,
                                      LangUtils.getCurrentLang(),
                                      translocalGeotreeType
                                  )
                                : [];

                            return of(
                                updateQuickSelectedItemsForRegionsTransformEditor(
                                    editorId,
                                    mapQuickSelectedItemsForRegionsTransformEditor(
                                        regions,
                                        regionTree,
                                        topRegionPresetsResponse.data
                                    ),
                                    false
                                )
                            );
                        }
                    ),
                    catchError(
                        (err: Error | ApiMethodExecutingError): Observable<Action> => {
                            const message = err instanceof ApiMethodExecutingError ? err.getMessage() : err.message;

                            try {
                                fireRegionsMassEditMetrika({
                                    getTopRegions: {
                                        errors: message
                                    }
                                });
                            } catch (e) {
                                // do nothing
                            }

                            return EMPTY;
                        }
                    )
                );
            }
        )
    );
};
